import React from 'react';
import { InfoIcon } from 'lucide-react';
import { useProto } from '../../contexts/PrototypeContext';
import { roleRemaining, roleStatus, type RoleStatus } from '../../utils/capacity';
import { FieldTag } from '../shared/FieldTag';
import { Annotation } from '../shared/Annotation';
const statusChip: Record<RoleStatus, {
  label: string;
  cls: string;
}> = {
  available: {
    label: 'Available',
    cls: 'bg-status-okBg text-status-okInk'
  },
  low: {
    label: '1 left',
    cls: 'bg-status-lowBg text-status-lowInk'
  },
  exhausted: {
    label: 'Exhausted',
    cls: 'bg-status-outBg text-status-outInk'
  },
  none: {
    label: 'Not offered',
    cls: 'bg-status-noneBg text-status-noneInk'
  }
};
export function RoleCapacityTable() {
  const {
    state,
    summary,
    actions
  } = useProto();
  const {
    roles,
    coachCreditEnabled
  } = state.config;
  const totalClaimed = roles.reduce((s, r) => s + r.claimed, 0);
  const totalRemaining = roles.reduce((s, r) => s + roleRemaining(r), 0);
  const ownHold = state.reservation.status === 'reserved' ? 1 : 0;
  return <div data-id="element-126" data-mp="36o2pcwixtisj">
      <h4 className="font-medium text-connect-ink" data-id="element-127" data-mp="36ombkpfmetcd">Discount opportunities by role</h4>
      <p className="mt-0.5 text-sm text-connect-muted" data-id="element-128" data-mp="31rm1m3fnh6fz">
        Limit how many volunteer discounts this program can give. Claimed counts update when families complete checkout.
      </p>

      <div className="mt-3 overflow-hidden rounded-lg border border-connect-line" data-id="element-129" data-mp="2rr3f10wpbbud">
        <table className="w-full text-sm" data-id="element-130" data-mp="2nqrstjoox3gs">
          <thead className="bg-connect-canvas text-left text-xs text-connect-muted" data-id="element-131" data-mp="2nrukhttu79xr">
            <tr data-id="element-132" data-mp="2iy8hyuhe0mfo">
              <th scope="col" className="px-4 py-2.5 font-medium" data-id="element-133" data-mp="2ixqat2t6snsz">Role</th>
              <th scope="col" className="px-4 py-2.5 font-medium" data-id="element-134" data-mp="2e1hyryaadaq1">
                <span className="flex items-center gap-1.5" data-id="element-135" data-mp="2925cpfr8o7oi">Discount Opportunities <FieldTag kind="editable" data-id="element-136" data-mp="28zspan2pp542" /></span>
              </th>
              <th scope="col" className="px-4 py-2.5 font-medium" data-id="element-137" data-mp="2420yjwdszp1g">
                <span className="flex items-center gap-1.5" data-id="element-138" data-mp="200p5thzmunp1">Discounts Claimed <FieldTag kind="readonly" data-id="element-139" data-mp="203bddhmix379" /></span>
              </th>
              <th scope="col" className="px-4 py-2.5 font-medium" data-id="element-140" data-mp="1peadux8tn8zm">
                <span className="flex items-center gap-1.5" data-id="element-141" data-mp="1uf6pjwzezzfl">Discounts Remaining <FieldTag kind="calculated" data-id="element-142" data-mp="1uckazde3wjnv" /></span>
              </th>
              <th scope="col" className="px-4 py-2.5 font-medium" data-id="element-143" data-mp="1ggzzv44md5r0">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-connect-line" data-id="element-144" data-mp="1lgcmpovhqctl">
            {roles.map(r => {
            const error = r.opportunities < r.claimed;
            const chip = statusChip[roleStatus(r)];
            return <tr key={r.id} data-id="element-145" data-mp="1ze9jomduwl8s">
                  <th scope="row" className="px-4 py-3 text-left font-medium text-connect-ink" data-id="element-146" data-mp="1zcyk6yrg1znh">{r.label}</th>
                  <td className="px-4 py-3 align-top" data-id="element-147" data-mp="24bt8gg86kgez">
                    <input type="number" min={r.claimed} value={r.opportunities} aria-label={`${r.label} discount opportunities`} aria-invalid={error} aria-describedby={error ? `${r.id}-err` : undefined} onChange={e => actions.updateRoleOpportunities(r.id, Math.max(0, parseInt(e.target.value || '0', 10) || 0))} className={`w-24 rounded border px-2.5 py-1.5 tabular-nums text-connect-ink outline-none focus:ring-1 ${error ? 'border-connect-danger focus:ring-connect-danger' : 'border-gray-300 focus:border-connect-blue focus:ring-connect-blue'}`} data-id="element-148" data-mp="2s2ynwua7l6or" />
                    {error && <p id={`${r.id}-err`} className="mt-1 text-xs text-connect-danger" data-id="element-149" data-mp="2x8k99ogczasm">Can't be lower than claimed ({r.claimed}).</p>}
                  </td>
                  <td className="px-4 py-3 align-top" data-id="element-150" data-mp="cgs8b2hmig0h">
                    <span className="inline-block w-16 rounded bg-connect-canvas px-2.5 py-1.5 tabular-nums text-connect-muted" data-id="element-151" data-mp="cjeg938fexj8">{r.claimed}</span>
                  </td>
                  <td className="px-4 py-3 align-top text-base font-semibold tabular-nums text-connect-ink" data-id="element-152" data-mp="2d7sry81jk4fh">{roleRemaining(r)}</td>
                  <td className="px-4 py-3 align-top" data-id="element-153" data-mp="2i7ol1c8pabg9">
                    <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${chip.cls}`} data-id="element-154" data-mp="2n4f84jixa7b6">{chip.label}</span>
                  </td>
                </tr>;
          })}
            <tr className="bg-connect-canvas/60 font-medium text-connect-ink" data-id="element-155" data-mp="2w4bvmhtkt04n">
              <th scope="row" className="px-4 py-2.5 text-left" data-id="element-156" data-mp="313wqoh5mkoc3">Role totals</th>
              <td className="px-4 py-2.5 tabular-nums" data-id="element-157" data-mp="35v6erdbizjlv">{summary.totalOpportunities}</td>
              <td className="px-4 py-2.5 tabular-nums" data-id="element-158" data-mp="35xiy9uwxm211">{totalClaimed}</td>
              <td className="px-4 py-2.5 tabular-nums" data-id="element-159" data-mp="3atgd1wsjr0bp">{totalRemaining}</td>
              <td data-id="element-160" data-mp="3ewwagvcfx4pg" />
            </tr>
          </tbody>
          <tfoot className="border-t-2 border-connect-line text-connect-ink" data-id="element-161" data-mp="3juxqf7ekknwp">
            <tr data-id="element-162" data-mp="3jsv2qu0w36v5">
              <th scope="row" colSpan={3} className="px-4 py-2 text-left font-normal text-connect-muted" data-id="element-163" data-mp="187krizqv70ce">
                Claimed at checkout, role not yet assigned
              </th>
              <td className="px-4 py-2 tabular-nums" data-id="element-164" data-mp="13am04oyl57hw">−{state.unassignedClaims}</td>
              <td data-id="element-165" data-mp="z764nvy95d6s" />
            </tr>
            <tr data-id="element-166" data-mp="u7t98mtbexsl">
              <th scope="row" colSpan={3} className="px-4 py-2 text-left font-normal text-connect-muted" data-id="element-167" data-mp="u8cn9sfuh21c">
                Active reservations held in customer carts{ownHold ? ' (incl. 1 in this prototype session)' : ''}
              </th>
              <td className="px-4 py-2 tabular-nums" data-id="element-168" data-mp="1mx0fk7z0groe">−{state.otherReservations + ownHold}</td>
              <td data-id="element-169" data-mp="1i47b7l7gflcu" />
            </tr>
            <tr className="bg-connect-blueSoft" data-id="element-170" data-mp="1i3fhyh1bxzpq">
              <th scope="row" colSpan={3} className="px-4 py-3 text-left font-medium" data-id="element-171" data-mp="1d5m7hbszujjn">Available to new customers</th>
              <td className="px-4 py-3 text-lg font-semibold tabular-nums" data-id="element-172" data-mp="2anmb4zrtwjhi">{summary.available}</td>
              <td className="px-4 py-3" data-id="element-173" data-mp="25tryggan54rx"><FieldTag kind="calculated" data-id="element-174" data-mp="25ti4khmh6dgy" /></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {summary.totalOpportunities === 0 && <p className="mt-3 flex items-start gap-2 rounded-md bg-status-lowBg px-3 py-2.5 text-sm text-status-lowInk" data-id="element-175" data-mp="2tpd29ei4lpq8">
          <InfoIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-176" data-mp="2fz18lb5w7iwz" />
          Add opportunities to at least one role. Until then, customers won't see the discount offer.
        </p>}
      {summary.totalOpportunities > 0 && summary.available === 0 && <p className="mt-3 flex items-start gap-2 rounded-md bg-status-outBg px-3 py-2.5 text-sm text-status-outInk" data-id="element-177" data-mp="2b183ymk8mwyt">
          <InfoIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-178" data-mp="1x201nkss38qe" />
          All discount opportunities are claimed or held. Customers won't see the offer
          {coachCreditEnabled ? '; Automated Coach Credit displays instead' : ''}. Increase opportunities to reopen it.
        </p>}

      <Annotation kind="unresolved" title="Decision #2 · how a customer counts against a role's capacity" className="mt-3" data-id="element-179" data-mp="3vfi752fhqyx8">
        At registration the customer's eventual role (Head Coach, Assistant, Helper) isn't known, so the rule for which role's opportunities a reservation or claim consumes is undefined. This prototype only shows them at program level and assumes availability = total opportunities − all claims − active reservations. Needs a product + engineering decision.
      </Annotation>
    </div>;
}